import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AppShell from '../chrome/AppShell';
import useAuthenticatedApi from '../../Authentication/components/hooks/useAuthenticatedApi';
import {
    Segmented,
    TCard,
    TEmpty,
    TFoot,
    Toolbar,
    TRow,
    TSkeleton,
} from '../components/table';
import { ConfirmDialog, Dialog, Field, TextInput } from '../components/forms';
import { PageHeadActions, ViewSwitch } from '../components/nav';
import { useNavigate } from 'react-router-dom';
import Timeline, { TLBooking, TLRow } from '../components/timeline';
import { addDays, dayStart, endDayOf, mondayOf } from '../components/calendar';
import { SubscribeButton } from '../components/feeds';
import { useEventDetails, useProductionRange, useView } from '../lib/schedule';
import { SANDBOX_DATA } from '../lib/sandbox';
import '../calendar.css';
import { dateRangeLabel, humanize } from '../lib/jobs';

/*
 * Staff roster. Assignments are edited on activations, not here.
 * The server sends invites on a person's first assignment.
 */

const fullName = (s: any) =>
    [s.firstName, s.lastName].filter(Boolean).join(' ') || s.email || 'Unnamed';
/** Minimal CSV: quoted fields may hold commas, "" is a literal quote. */
function parseCsv(text: string): string[][] {
    const rows: string[][] = [];
    let row: string[] = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (quoted) {
            if (c === '"' && text[i + 1] === '"') {
                cell += '"';
                i++;
            } else if (c === '"') quoted = false;
            else cell += c;
        } else if (c === '"') quoted = true;
        else if (c === ',') {
            row.push(cell);
            cell = '';
        } else if (c === '\n' || c === '\r') {
            if (c === '\r' && text[i + 1] === '\n') i++;
            row.push(cell);
            if (row.some((v) => v.trim())) rows.push(row);
            row = [];
            cell = '';
        } else cell += c;
    }
    row.push(cell);
    if (row.some((v) => v.trim())) rows.push(row);
    return rows;
}

const place = (s: any) => [s.city, s.state].filter(Boolean).join(', ');

const VIEWS = ['list', 'schedule'] as const;

export default function StaffV3() {
    const api = useAuthenticatedApi();
    const navigate = useNavigate();
    const [view, setView] = useView<(typeof VIEWS)[number]>('staff', VIEWS, 'list');
    const [rows, setRows] = useState<any[] | null>(null);
    const [failed, setFailed] = useState(false);
    const [query, setQuery] = useState('');
    const [role, setRole] = useState('all');
    const [toast, setToast] = useState<string | null>(null);

    const load = useCallback(() => {
        api.get('/production/staff')
            .then((r: any) => {
                setRows(r.data ?? []);
                setFailed(false);
            })
            .catch(() => setFailed(true));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useEffect(load, [load]);
    useEffect(() => {
        if (!toast) return;
        const t = window.setTimeout(() => setToast(null), 5000);
        return () => window.clearTimeout(t);
    }, [toast]);

    /* Top three roles become the inline filter. */
    const roles = useMemo(() => {
        const counts = new Map<string, number>();
        (rows ?? []).forEach((s) =>
            (s.roles ?? []).forEach((r: string) => counts.set(r, (counts.get(r) ?? 0) + 1)),
        );
        return Array.from(counts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([r]) => r);
    }, [rows]);

    const q = query.trim().toLowerCase();
    const visible = useMemo(
        () =>
            (rows ?? [])
                .filter(
                    (s) =>
                        !q ||
                        fullName(s).toLowerCase().includes(q) ||
                        (s.email ?? '').toLowerCase().includes(q) ||
                        place(s).toLowerCase().includes(q),
                )
                .filter((s) => role === 'all' || (s.roles ?? []).includes(role))
                .sort((a, b) => fullName(a).localeCompare(fullName(b))),
        [rows, q, role],
    );

    /* Schedule: crew from each event's staff assignments; declined and cancelled are hidden.
       Time off has no backend yet (fake data only). */
    const [tlStart, setTlStart] = useState(() => mondayOf(Date.now()));
    const range = useProductionRange(addDays(tlStart, -31), addDays(tlStart, 13), view === 'schedule');
    const ids = (range.rows ?? []).filter((r) => !r.isProvisional).map((r) => r.activationId);
    const details = useEventDetails(ids, ['prod']);
    const [off, setOff] = useState<any[]>([]);
    useEffect(() => {
        if (view !== 'schedule' || !SANDBOX_DATA) return;
        api.get('/dev/time-off')
            .then((r: any) => setOff(r.data ?? []))
            .catch(() => setOff([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [view]);
    const tlRows: TLRow[] = visible.map((s) => ({
        id: s.id,
        name: fullName(s),
        sub: [(s.roles ?? []).join(', '), s.city].filter(Boolean).join(' · '),
        onOpen: () => open(s),
    }));
    const tlBookings: TLBooking[] = [
        ...(range.rows ?? []).filter((r) => r.startAt).flatMap((r) =>
            ((details[r.activationId]?.prod?.staffAssignments ?? []) as any[])
                .filter((a) => a.status !== 'declined' && a.status !== 'cancelled')
                .map((a) => ({
                    id: `${r.activationId}-${a.staffId}-${a.role}`,
                    rowId: a.staffId,
                    name: r.name,
                    startDay: dayStart(a.assignmentStart ?? r.startAt!),
                    endDay: endDayOf(a.assignmentEnd ?? r.endAt ?? r.startAt!, dayStart(a.assignmentStart ?? r.startAt!)),
                    kind: a.status === 'pending' ? ('tentative' as const) : ('confirmed' as const),
                    note: a.role === 'ba' ? 'Brand ambassador' : humanize(a.role),
                    href: `/activations/${r.activationId}`,
                })),
        ),
        ...off.map((o, i) => ({
            id: `off-${o.staffId}-${i}`,
            rowId: o.staffId,
            name: 'Time off',
            startDay: dayStart(`${o.from}T12:00:00`),
            endDay: dayStart(`${o.to}T12:00:00`),
            kind: 'off' as const,
            note: o.note,
        })),
    ];

    const [openId, setOpenId] = useState<number | null>(null);
    const [detail, setDetail] = useState<any>(null);
    /* a late reply for someone else (or a closed dialog) is ignored */
    const wanted = useRef<number | null>(null);
    const open = (s: any) => {
        wanted.current = s.id;
        setOpenId(s.id);
        setDetail({ ...s, loading: true });
        api.get(`/production/staff/${s.id}`)
            .then((r: any) => wanted.current === s.id && setDetail(r.data))
            .catch(() => wanted.current === s.id && setDetail({ ...s, loadFailed: true }));
    };
    const closeDetail = () => {
        wanted.current = null;
        setOpenId(null);
    };

    const blank = { firstName: '', lastName: '', email: '', phone: '' };
    const [adding, setAdding] = useState(false);
    const [fields, setFields] = useState(blank);
    const [addError, setAddError] = useState('');
    const [saving, setSaving] = useState(false);
    const emailOk = /^\S+@\S+\.\S+$/.test(fields.email.trim());
    const submitAdd = async () => {
        if (!fields.firstName.trim() || !emailOk || saving) return;
        setSaving(true);
        try {
            await api.post('/production/staff', {
                firstName: fields.firstName.trim(),
                lastName: fields.lastName.trim() || null,
                email: fields.email.trim(),
                phone: fields.phone.trim() || null,
            });
            setAdding(false);
            setFields(blank);
            setToast(`Added ${fields.firstName.trim()}. They’re invited on their first assignment.`);
            load();
        } catch {
            setAddError('Couldn’t add this person — is that email already on the roster?');
        } finally {
            setSaving(false);
        }
    };

    /* CSV import: columns map by header name; roles/skills/languages split on ";". */
    const fileRef = useRef<HTMLInputElement>(null);
    /* same rule as the v2 page: the server refuses people with assignments */
    const [removing, setRemoving] = useState<any>(null);
    const remove = async (s: any) => {
        try {
            await api.delete(`/production/staff/${s.id}`);
            setToast(`Removed ${fullName(s)}.`);
            if (openId === s.id) closeDetail();
            load();
        } catch {
            setToast(`Couldn’t remove ${fullName(s)} — they have assignments.`);
        }
    };
    const [pendingImport, setPendingImport] = useState<Record<string, any>[] | null>(null);
    const importCsv = async (file: File) => {
        const table = parseCsv(await file.text());
        if (table.length < 2) {
            setToast('That file has no rows to import.');
            return;
        }
        const header = table[0].map((h) => h.trim());
        const parsed = table.slice(1).map((cells) => {
            const row: Record<string, any> = {};
            header.forEach((key, i) => {
                const value = (cells[i] ?? '').trim();
                if (!value) return;
                row[key] = ['roles', 'skills', 'languages'].includes(key)
                    ? value.split(';').map((v) => v.trim())
                    : value;
            });
            return row;
        });
        /* nothing is saved until the count is confirmed */
        setPendingImport(parsed);
    };
    const confirmImport = async () => {
        const parsed = pendingImport;
        setPendingImport(null);
        if (!parsed) return;
        try {
            const r: any = await api.post('/production/staff/bulk', { rows: parsed });
            const created = r?.data?.created?.length ?? 0;
            const skipped = r?.data?.errors?.length ?? 0;
            setToast(`Imported ${created} ${created === 1 ? 'person' : 'people'}${skipped ? ` · ${skipped} skipped` : ''}.`);
            load();
        } catch {
            setToast('Import failed — check the header row (firstName, lastName, email, …).');
        }
    };

    return (
        <AppShell>
            <PageHeadActions
                title="Staff"
                sub={rows ? `${rows.length} people · contractors and staff` : 'Contractors and staff'}
                actions={
                    <>
                        <ViewSwitch
                            value={view}
                            onChange={setView}
                            options={[
                                { value: 'list', label: 'List' },
                                { value: 'schedule', label: 'Schedule' },
                            ]}
                        />
                        {view === 'schedule' && <SubscribeButton />}
                        <button className="tb-btn" onClick={() => fileRef.current?.click()}>
                            Import CSV
                        </button>
                        <input
                            ref={fileRef}
                            type="file"
                            accept=".csv,text/csv"
                            hidden
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) importCsv(f);
                                e.target.value = '';
                            }}
                        />
                        <button
                            className="tb-new"
                            onClick={() => {
                                setAddError('');
                                setAdding(true);
                            }}
                        >
                            Add person
                        </button>
                    </>
                }
            />
            <Toolbar
                searchValue={query}
                onSearch={setQuery}
                searchPlaceholder="Search name, email, or city"
                filterCount={0}
                inline={
                    roles.length > 0 && (
                        <Segmented
                            label="Role"
                            options={[
                                { value: 'all', label: 'All' },
                                ...roles.map((r) => ({ value: r, label: r })),
                            ]}
                            value={role}
                            onChange={setRole}
                        />
                    )
                }
            />

            {view === 'schedule' && !failed && (
                range.failed ? (
                    <TCard>
                        <TEmpty message="Couldn’t load the schedule." actionLabel="Retry" onAction={range.reload} />
                    </TCard>
                ) : (
                    <Timeline
                        title="Crew schedule"
                        what="people"
                        rows={tlRows}
                        bookings={tlBookings}
                        start={tlStart}
                        onShift={(n) => setTlStart((t) => addDays(t, 7 * n))}
                        onToday={() => setTlStart(mondayOf(Date.now()))}
                        navigate={navigate}
                        loading={rows === null || range.rows === null}
                    />
                )
            )}
            {failed && (
                <TCard>
                    <TEmpty message="Couldn’t load staff." actionLabel="Retry" onAction={load} />
                </TCard>
            )}
            {view === 'list' && !failed && rows === null && (
                <TCard>
                    <TSkeleton rows={4} />
                </TCard>
            )}
            {view === 'list' && !failed && rows !== null && visible.length === 0 && (
                <TCard>
                    <TEmpty
                        message={
                            rows.length === 0
                                ? 'No one on the roster yet.'
                                : q
                                  ? `Nothing matches “${query}”.`
                                  : `No ${role}s yet.`
                        }
                        actionLabel={rows.length === 0 ? 'Add person' : undefined}
                        onAction={() => setAdding(true)}
                    />
                </TCard>
            )}
            {view === 'list' && !failed && visible.length > 0 && (
                <TCard>
                    {visible.map((s, i) => {
                        const rolesLabel = (s.roles ?? []).join(', ');
                        const gigs = Number(s.assignmentCount ?? 0);
                        return (
                            <TRow
                                key={s.id}
                                index={i}
                                name={fullName(s)}
                                meta={[s.email, place(s)].filter(Boolean).join(' · ')}
                                metaDate={rolesLabel}
                                date={rolesLabel}
                                status={`${gigs} ${gigs === 1 ? 'gig' : 'gigs'}`}
                                onOpen={() => open(s)}
                                menuItems={[
                                    { label: 'Details', onClick: () => open(s) },
                                    ...(s.email ? [{ label: 'Email', onClick: () => (window.location.href = `mailto:${s.email}`) }] : []),
                                    ...(s.phone ? [{ label: 'Call', onClick: () => (window.location.href = `tel:${s.phone}`) }] : []),
                                    { label: 'Remove…', onClick: () => setRemoving(s) },
                                ]}
                            />
                        );
                    })}
                </TCard>
            )}
            {!failed && rows !== null && (
                <TFoot>
                    {q || role !== 'all'
                        ? `${visible.length} of ${rows.length} · filtered`
                        : `${rows.length} people`}
                </TFoot>
            )}

            <Dialog
                open={openId !== null}
                title={detail ? fullName(detail) : ''}
                onClose={closeDetail}
                footer={
                    <button type="button" className="tb-btn" onClick={closeDetail}>
                        Close
                    </button>
                }
            >
                {detail && (
                    <div className="person">
                        <p className="pg-sub" style={{ margin: '0 0 12px' }}>
                            {[(detail.roles ?? []).join(', '), place(detail)].filter(Boolean).join(' · ')}
                        </p>
                        <div className="p-actions">
                            {detail.email && (
                                <a className="tb-btn" href={`mailto:${detail.email}`}>Email</a>
                            )}
                            {detail.phone && (
                                <a className="tb-btn" href={`tel:${detail.phone}`}>Call</a>
                            )}
                        </div>
                        <div className="frow"><span className="fl">Email</span><span className="fv">{detail.email ?? '—'}</span></div>
                        <div className="frow"><span className="fl">Phone</span><span className="fv">{detail.phone ?? '—'}</span></div>
                        {(detail.skills ?? []).length > 0 && (
                            <div className="frow"><span className="fl">Skills</span><span className="fv">{detail.skills.join(', ')}</span></div>
                        )}
                        {(detail.languages ?? []).length > 0 && (
                            <div className="frow"><span className="fl">Languages</span><span className="fv">{detail.languages.join(', ')}</span></div>
                        )}
                        {detail.notes && (
                            <div className="frow"><span className="fl">Notes</span><span className="fv">{detail.notes}</span></div>
                        )}
                        <p className="s-lab" style={{ margin: '14px 0 6px' }}>Gigs</p>
                        {detail.loading ? (
                            <p className="pg-sub" style={{ margin: 0 }}>Loading…</p>
                        ) : detail.loadFailed ? (
                            <p className="pg-sub" style={{ margin: 0 }}>Couldn’t load gigs.</p>
                        ) : (detail.assignments ?? []).length === 0 ? (
                            <p className="pg-sub" style={{ margin: 0 }}>No gigs yet — an invite goes out with the first one.</p>
                        ) : (
                            (detail.assignments ?? []).map((g: any) => (
                                <div className="trow2" key={g.id}>
                                    <span className="nm2">
                                        {g.activationName}
                                        <span className="sub2">
                                            {[g.role, dateRangeLabel(g.startAt)].filter(Boolean).join(' · ')}
                                        </span>
                                    </span>
                                    <span className="right">{humanize(g.status)}</span>
                                </div>
                            ))
                        )}
                    </div>
                )}
            </Dialog>

            <Dialog
                open={adding}
                title="Add person"
                onClose={() => setAdding(false)}
                onSubmit={submitAdd}
                footer={
                    <>
                        <button type="button" className="tb-btn" onClick={() => setAdding(false)}>
                            Cancel
                        </button>
                        <button type="submit" className="tb-new" disabled={!fields.firstName.trim() || !emailOk || saving}>
                            {saving ? 'Adding…' : 'Add person'}
                        </button>
                    </>
                }
            >
                <div className="two">
                    <Field label="First name">
                        <TextInput autoFocus autoComplete="given-name" value={fields.firstName} onChange={(e) => setFields({ ...fields, firstName: e.target.value })} />
                    </Field>
                    <Field label="Last name">
                        <TextInput autoComplete="family-name" value={fields.lastName} onChange={(e) => setFields({ ...fields, lastName: e.target.value })} />
                    </Field>
                </div>
                <Field
                    label="Email"
                    help="Their invite goes here when they get their first gig."
                    error={fields.email && !emailOk ? 'That doesn’t look like an email address.' : addError || undefined}
                >
                    <TextInput type="email" inputMode="email" autoComplete="email" value={fields.email} onChange={(e) => setFields({ ...fields, email: e.target.value })} />
                </Field>
                <Field label="Phone (optional)">
                    <TextInput type="tel" inputMode="tel" autoComplete="tel" value={fields.phone} onChange={(e) => setFields({ ...fields, phone: e.target.value })} />
                </Field>
            </Dialog>

            {toast && (
                <div className="toasthost" role="status" aria-live="polite">
                    <div className="toast">
                        <span className="t1" style={{ flex: 1, minWidth: 0 }}>{toast}</span>
                    </div>
                </div>
            )}
            <ConfirmDialog
                open={removing !== null}
                title={`Remove ${removing ? fullName(removing) : ''}?`}
                body="They leave the roster. People with assignments can’t be removed. This can’t be undone."
                actionLabel="Remove"
                onConfirm={() => removing && remove(removing)}
                onClose={() => setRemoving(null)}
            />
            <ConfirmDialog
                open={pendingImport !== null}
                title={`Import ${pendingImport?.length ?? 0} ${pendingImport?.length === 1 ? 'person' : 'people'}?`}
                body={`From the file: ${(pendingImport ?? []).slice(0, 3).map((r) => [r.firstName, r.lastName].filter(Boolean).join(' ') || r.email || '—').join(', ')}${(pendingImport?.length ?? 0) > 3 ? ', …' : ''}. People with an email already on the roster are skipped.`}
                actionLabel="Import"
                danger={false}
                onConfirm={confirmImport}
                onClose={() => setPendingImport(null)}
            />
        </AppShell>
    );
}
